import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { resetCart } from "../../redux/cartSlice";
import { emptyCart } from "../../assets/images/index";
import ItemCard from "./ItemCard";
import { IoClose } from "react-icons/io5";
import { sendProducts } from "../../redux/paymentSlice";
import { toast } from "react-toastify";
import { toggleCart } from "../../redux/shopSlice";

const getToday = () => {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
};

const Cart = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const products = useSelector((state) => state.cartReducer.products);
  const [totalAmt, setTotalAmt] = useState(0);
  const [deliveryDate, setDeliveryDate] = useState(getToday);
  const cartRef = useRef(null);
  const today = getToday();

  useEffect(() => {
    let price = products.reduce(
      (total, item) =>
        total + Number(item?.price?.replace(",", "")) * item.quantity,
      0,
    );
    setTotalAmt(price);
  }, [products]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (cartRef.current && !cartRef.current.contains(event.target)) {
        onClose(); // Close the cart when clicking outside
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  const handleResetCart = () => {
    dispatch(resetCart());
    setDeliveryDate(getToday());
  };

  const handleSendProducts = () => {
    dispatch(sendProducts({ products, deliveryDate }))
      .unwrap()
      .then((response) => {
        toast.success("Order placed successfully", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
        });
        dispatch(toggleCart());
        dispatch(resetCart());
        setDeliveryDate(getToday());
        if (response.order) {
          navigate("/orders/" + response?.order);
        }
      })
      .catch((error) => {
        toast.error("Failed to place the order. Please try again.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
        });
      });
  };
  return (
    <div
      ref={cartRef}
      className={`fixed top-0 right-0 h-screen w-80 lg:w-[30rem] bg-white shadow-lg z-50 transform ${
        isOpen ? "translate-x-0" : "translate-x-full"
      } transition-transform duration-300 ease-in-out flex flex-col`}
    >
      {/* Drawer Header */}
      <div className="flex justify-between items-center px-4 py-3 border-b flex-none">
        <h2 className="text-lg font-semibold">Your Cart</h2>
        <button
          onClick={onClose}
          className="text-2xl text-gray-500 hover:text-red-500"
        >
          <IoClose />
        </button>
      </div>

      {/* Cart Content */}
      <div className="flex-1 overflow-y-auto scrollbar-none px-2 py-2">
        {products.length > 0 ? (
          <div className="space-y-4">
            {products.map((item) => (
              <ItemCard
                key={`${item?._id}-${item?.color?.g_color}-${item?.size?.g_size}-${item?.lot?.lotno}`}
                item={item}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center space-y-4 mt-20">
            <img className="w-40 rounded-lg" src={emptyCart} alt="Empty Cart" />
            <h1 className="text-lg font-medium">Your Cart is Empty</h1>
            <p className="text-sm text-gray-500">
              It looks like you haven't added anything yet.
            </p>
            <Link to="/shop" onClick={onClose}>
              <button className="px-6 py-2 bg-primeColor text-white text-sm rounded hover:bg-black duration-300">
                Continue Shopping
              </button>
            </Link>
          </div>
        )}
      </div>

      {/* Cart Footer */}
      {products.length > 0 && (
        <div className="p-4 border-t bg-white flex-none">
          <div className="flex justify-between items-center mb-3">
            <label htmlFor="delivery-date" className="text-base font-semibold">
              Delivery Date
            </label>
            <input
              id="delivery-date"
              type="date"
              min={today}
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700 focus:outline-none focus:border-primeColor"
            />
          </div>
          <div className="flex justify-between items-center mb-4">
            <span className="text-base font-semibold">Total</span>
            <span className="text-lg font-bold">
              {sessionStorage.getItem("cur")}{" "}
              <span className="font-medium">
                {Intl.NumberFormat("en-US").format(totalAmt)}
              </span>
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <button
              onClick={handleResetCart}
              className="w-1/2 py-2 text-sm font-medium text-red-600 border border-red-500 rounded hover:bg-red-100"
            >
              Reset Cart
            </button>
            <div className="w-1/2">
              <button
                onClick={() => handleSendProducts()}
                className="w-full py-2 bg-primeColor text-white text-sm rounded hover:bg-blue-800 duration-300"
              >
                Place Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
